import { z } from 'zod';
import { jsonObject, riskLevel, uuid } from './primitives';
import { toolResult } from './tool';
import { normalizedError } from './errors';

/**
 * ToolGateway request/response (§8.2, §9.1). Every side effect flows through this
 * one endpoint (I2). Requests are authenticated by a short-lived per-run token
 * scoped to the run's allow-list (§8.4).
 */
export const gatewayExecuteRequest = z.object({
  runId: uuid,
  /** Deterministic step idempotency is derived server-side as `runId:stepNo` (§9.1). */
  tool: z.string().min(1),
  arguments: jsonObject,
  connectionId: uuid.optional(),
});
export type GatewayExecuteRequest = z.infer<typeof gatewayExecuteRequest>;

/**
 * Gateway response, discriminated by `outcome`. Includes the `approval_required`
 * and `denied` variants called out in §8.2. `ok`/`failed` mirror ToolResult;
 * `ambiguous` is the non-idempotent-failure case that BLOCKS the run (I8/§9.4).
 */
export const gatewayExecuteResponse = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('ok'),
    stepNo: z.number().int().positive(),
    result: toolResult,
  }),
  z.object({
    outcome: z.literal('approval_required'),
    approvalId: uuid,
    risk: riskLevel,
    reason: z.string().optional(),
  }),
  z.object({
    outcome: z.literal('denied'),
    reason: z.string(),
    /** e.g. 'kill_switch', 'agent_disabled', 'tool_not_granted', 'connection_unhealthy'. */
    code: z.string(),
  }),
  z.object({
    outcome: z.literal('ambiguous'),
    stepNo: z.number().int().positive(),
    reason: z.string(),
  }),
  z.object({
    outcome: z.literal('failed'),
    stepNo: z.number().int().positive().optional(),
    error: normalizedError,
  }),
]);
export type GatewayExecuteResponse = z.infer<typeof gatewayExecuteResponse>;
